import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SectionHead, Crosshair } from "./Reveal";
import { useLang } from "../../i18n/LanguageContext";
import { Loader2, Send, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", email: "", phone: "", country: "", solution: "", fleet_size: "", message: "" };

const inputCls =
  "w-full border border-technical bg-white px-4 py-3 text-sm text-ink placeholder:text-[#9ca3af] outline-none transition-colors duration-200 focus:border-red-brand";

export const ContactSection = () => {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(t.contact.successTitle, { description: t.contact.successBody });
      setForm(EMPTY);
    } catch (err) {
      toast.error(t.contact.errorTitle, { description: t.contact.errorBody });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative bg-canvas bp-grid py-24 sm:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow={t.contact.eyebrow} title={t.contact.title} desc={t.contact.desc} testid="contact" />
            <div className="mt-10 space-y-3 font-mono-tech text-[11px] tracking-[0.15em] text-[#525964]">
              <p className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-red-brand rotate-45 inline-block" /> NAIROBI HQ — 01°17′S 36°49′E</p>
              <p className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-red-brand rotate-45 inline-block" /> KINSHASA — 04°19′S 15°18′E</p>
              <p className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-red-brand rotate-45 inline-block" /> DAR ES SALAAM — 06°47′S 39°12′E</p>
            </div>
            <div className="mt-8">
              <p className="eyebrow">{t.contact.phonesLabel}</p>
              <div className="mt-3 space-y-2">
                {t.contact.phones.map((p) => (
                  <a
                    key={p}
                    data-testid={`contact-phone-${p.replace(/[^0-9]/g, "")}`}
                    href={`tel:${p.replace(/\s/g, "")}`}
                    className="block font-mono-tech text-sm font-semibold text-ink transition-colors duration-200 hover:text-red-brand"
                  >
                    {p}
                  </a>
                ))}
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${t.contact.contactEmail}`}
                  className="block font-mono-tech text-sm font-semibold text-red-brand transition-colors duration-200 hover:text-ink"
                >
                  {t.contact.contactEmail}
                </a>
                <a
                  data-testid="contact-website-link"
                  href="https://www.margtor.africa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-mono-tech text-sm font-semibold text-red-brand transition-colors duration-200 hover:text-[#5f1019]"
                >
                  www.margtor.africa
                </a>
              </div>
              <a
                data-testid="contact-brief-download"
                href={t.contact.briefFile}
                download
                className="chamfer-sm mt-7 inline-flex items-center gap-2.5 border border-[#16181b]/30 px-5 py-3 font-mono-tech text-[11px] font-semibold uppercase tracking-[0.15em] text-ink transition-all duration-200 hover:border-red-brand hover:text-red-brand hover:-translate-y-0.5"
              >
                <Download className="h-4 w-4" />
                {t.contact.brief}
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            <Crosshair className="-top-2 -left-2" />
            <Crosshair className="-bottom-2 -right-2" />
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-technical bg-white p-7 sm:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="eyebrow block mb-2">{t.contact.name} *</label>
                  <input data-testid="contact-input-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder="Juma Mwangi" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t.contact.company} *</label>
                  <input data-testid="contact-input-company" required minLength={2} value={form.company} onChange={set("company")} className={inputCls} placeholder="Lake Fleet Co." />
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t.contact.email} *</label>
                  <input data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="name@company.com" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t.contact.phone} *</label>
                  <input data-testid="contact-input-phone" required minLength={6} value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+254 7XX XXX XXX" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t.contact.country} *</label>
                  <select data-testid="contact-select-country" required value={form.country} onChange={set("country")} className={inputCls}>
                    <option value="" disabled>—</option>
                    {t.contact.countries.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t.contact.solution} *</label>
                  <select data-testid="contact-select-solution" required value={form.solution} onChange={set("solution")} className={inputCls}>
                    <option value="" disabled>—</option>
                    {t.contact.solutions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="eyebrow block mb-2">{t.contact.fleet}</label>
                  <input data-testid="contact-input-fleetsize" value={form.fleet_size} onChange={set("fleet_size")} className={inputCls} placeholder="e.g. 40 units / 200 kWh" />
                </div>
                <div className="sm:col-span-2">
                  <label className="eyebrow block mb-2">{t.contact.message} *</label>
                  <textarea data-testid="contact-textarea-message" required minLength={5} rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} />
                </div>
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="chamfer-sm mt-8 inline-flex items-center gap-3 bg-red-brand px-8 py-4 font-mono-tech text-xs font-semibold uppercase tracking-[0.18em] text-[#f8f7f4] transition-all duration-200 hover:bg-[#5f1019] hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? t.contact.sending : t.contact.submit}
              </button>
              <p data-testid="contact-status-message" className="mt-4 font-mono-tech text-[10px] tracking-[0.15em] text-[#9ca3af]">
                MGT-FORM-01 / SECURE TRANSMISSION
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
